import { Box, Typography, styled, Grid, Divider, Paper, useTheme, Tooltip } from '@mui/material';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { useLoanContext } from '../LoanContext';
import { infoTexts as rawInfoTexts } from '../../../constants/infoTexts';
import React from 'react';
import type { Loan } from '../../LoansDashboard';
import { formatDateToDDMMYYYY } from '../../../utils/dateUtils';
import { BRAND_TABLE_TEXT_COLOR } from '../../../styles/tableColors';

const infoTexts: Record<string, string> = rawInfoTexts;

// Simple formatting function for currency, percentage, and dates
const formatValue = (key: string, value: any): string => {
  if (value === undefined || value === null || value === '') {
    return '—';
  }

  // Date fields
  const dateFields = [
    'maturityDate', 'disbursementDate', 'nextInstallmentDate', 'nextPaymentDate',
    'createdDate', 'modifiedDate', 'dueDate', 'lastPaymentDate'
  ];
  if (dateFields.includes(key)) {
    return formatDateToDDMMYYYY(String(value));
  }

  // Currency fields
  const currencyFields = [
    'loanAmount', 'availableAmountForNextFunding', 'disbursedAmount',
    'principalRemaining', 'interestRemaining', 'feesRemaining',
    'amountDueTillCurrent', 'totalOutstandingAsOfDate', 'creditLimit',
    'totalLoanLimit', 'totalUtilizedAmount', 'propertyValuation', 'nextPaymentAmount'
  ];
  if (currencyFields.includes(key)) {
    const numValue = parseFloat(value);
    if (!isNaN(numValue)) {
      return `£${Number(numValue).toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
    }
  }

  // Percentage field
  if (key === 'interestRate') {
    const numValue = parseFloat(String(value));
    if (!isNaN(numValue)) {
      return `${numValue.toFixed(2)}%`;
    }
  }

  // Tenure field - add "mo."
  if (key === 'numberOfInstallments') {
    const numValue = parseFloat(String(value));
    if (!isNaN(numValue)) {
      return `${numValue} mo.`;
    }
  }

  // Billing frequency field - format
  if (key === 'frequencyOfLoanPayment') {
    const freqValue = String(value).toLowerCase();
    if (freqValue.includes('month')) return 'Monthly';
    if (freqValue.includes('week')) return 'Weekly';
    if (freqValue.includes('quarter')) return 'Quarterly';
    if (freqValue.includes('year')) return 'Yearly';
    return String(value);
  }

  // Display-only mapping for rate type
  if (key === 'rateType') {
    return String(value).trim().toLowerCase() === 'floating' ? 'Variable' : String(value);
  }

  return String(value);
};

const headerFieldsByLoanType: Record<string, { key: string; label: string }[]> = {
  'Term Loan': [
    { key: 'maturityDate', label: 'Maturity Date' },
    { key: 'frequencyOfLoanPayment', label: 'Billing Frequency' },
    { key: 'numberOfInstallments', label: 'Term' }
  ],
  'Working Capital Loan': [
    { key: 'maturityDate', label: 'Maturity Date' },
    { key: 'frequencyOfLoanPayment', label: 'Billing Frequency' },
    { key: 'numberOfInstallments', label: 'Term' }
  ],
  'Vehicle Loan': [
    { key: 'maturityDate', label: 'Maturity Date' },
    { key: 'vehicleMakeModel', label: 'Vehicle Make & Model' },
    { key: 'registrationNumber', label: 'Registration Number (masked)' }
  ],
  'Mortgage Loan': [
    { key: 'maturityDate', label: 'Maturity Date' },
    { key: 'frequencyOfLoanPayment', label: 'Billing Frequency' },
    { key: 'disbursementDate', label: 'Disbursement Date' }
  ],
  'Line of Credit': [
    { key: 'creditLimit', label: 'Credit Limit' },
    { key: 'frequencyOfLoanPayment', label: 'Billing Frequency' },
    { key: 'disbursedAmount', label: 'Utilized Amount' }
  ],
  'Master Facility': [
    { key: 'id', label: 'Facility ID' },
    { key: 'maturityDate', label: 'Maturity Date' },
    { key: 'numberOfChildLoans', label: 'No. of Child Loans' }
  ]
};

const SummaryCard = styled(Paper)(({ theme }) => ({
  background: theme.palette.background.paper,
  borderRadius: 16,
  padding: theme.spacing(4),
  color: theme.palette.text.primary,
  boxShadow: theme.palette.mode === 'dark' ? '0 4px 12px rgba(0,0,0,0.1)' : '0 4px 12px rgba(0,0,0,0.05)',
  maxWidth: 1500,
  width: '100%',
  margin: 0,
  alignSelf: 'flex-start',
}));

const FieldItem = ({ label, value, tooltip }: { label: string; value: React.ReactNode; tooltip?: string }) => (
  <Box sx={{
    display: 'grid',
    gridTemplateColumns: '1fr auto',
    gap: 2,
    alignItems: 'center',
    py: 1
  }}>
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
      <Typography
        sx={{
          color: BRAND_TABLE_TEXT_COLOR,
          fontSize: 16,
          fontFamily: "'Libre Franklin', sans-serif",
          fontWeight: 400
        }}
      >
        {label}
      </Typography>
      {tooltip && (
        <Tooltip title={tooltip}>
          <InfoOutlinedIcon sx={{ fontSize: 14, color: '#bdbdbd', cursor: 'pointer' }} />
        </Tooltip>
      )}
    </Box>
    <Typography
      sx={{
        color: BRAND_TABLE_TEXT_COLOR,
        fontWeight: 500,
        fontSize: 15,
        fontFamily: "'Libre Franklin', sans-serif",
        textAlign: 'right',
        minWidth: 'fit-content'
      }}
    >
      {value}
    </Typography>
  </Box>
);

const SummaryTab = () => {
  const { loan } = useLoanContext();
  const theme = useTheme();

  if (!loan) {
    return (
      <Box sx={{ p: 4, textAlign: 'center' }}>
        <Typography color="text.secondary">Loading loan data...</Typography>
      </Box>
    );
  }

  const loanData = loan;
  const loanType = loanData.productName || loanData.loanProductName || 'Term Loan';

  const getDefaultHeaderFields = () => ([
    { key: 'maturityDate', label: 'Maturity Date' },
    { key: 'frequencyOfLoanPayment', label: 'Billing Frequency' },
    { key: 'numberOfInstallments', label: 'Term' }
  ]);

  const headerFields = headerFieldsByLoanType[loanType] || getDefaultHeaderFields();

  // Force exact visual order for first row
  const displayOrder = ['maturityDate', 'frequencyOfLoanPayment', 'numberOfInstallments'];
  const firstRow = headerFields
    .filter(f => displayOrder.includes(f.key))
    .sort((a, b) => displayOrder.indexOf(a.key) - displayOrder.indexOf(b.key));

  return (
    <Box sx={{ width: '100%', mx: 'auto', mt: 4 }}>
      <SummaryCard>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: { xs: 4, md: 12 }, mb: 3 }}>
          <Box>
            <Typography sx={{ fontFamily: "'Libre Franklin', sans-serif", fontWeight: 500, fontSize: 16, color: BRAND_TABLE_TEXT_COLOR, mb: 0.5 }}>
              Loan Account
            </Typography>
            <Typography sx={{ fontWeight: 700, fontFamily: "'Libre Franklin', sans-serif", fontSize: 20, color: BRAND_TABLE_TEXT_COLOR, letterSpacing: 0.5 }}>
              {loanData?.name || 'N/A'}
            </Typography>
          </Box>
          <Box>
            <Typography sx={{ fontFamily: "'Libre Franklin', sans-serif", fontWeight: 500, fontSize: 16, color: BRAND_TABLE_TEXT_COLOR, mb: 0.5 }}>
              Loan Agreement Number
            </Typography>
            <Typography sx={{ fontWeight: 700, fontFamily: "'Libre Franklin', sans-serif", fontSize: 20, color: BRAND_TABLE_TEXT_COLOR, letterSpacing: 0.5 }}>
              {loanData?.loanAgreementNumber || 'N/A'}
            </Typography>
          </Box>
        </Box>

        <Divider sx={{ mb: 3, borderColor: theme.palette.mode === 'dark' ? '#444' : '#eee' }} />

        {/* First row: Maturity Date | Billing Frequency (center) | Tenure (right) */}
        <Grid container spacing={3} sx={{ mb: 3 }}>
          {firstRow.map(({ key, label }, i) => {
            const alignItemsSm = i === 0 ? 'flex-start' : i === 1 ? 'center' : 'flex-end';
            const textAlignSm = i === 0 ? 'left'       : i === 1 ? 'center' : 'right';
            const justifySm   = i === 0 ? 'flex-start' : i === 1 ? 'center' : 'flex-end';

            return (
              <Grid item xs={12} sm={4} key={key}>
                <Box
                  sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    minHeight: 56,
                    alignItems: { xs: 'flex-start', sm: alignItemsSm },
                    width: '100%',
                  }}
                >
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 0.5,
                      width: '100%',
                      justifyContent: { xs: 'flex-start', sm: justifySm },
                    }}
                  >
                    <Typography
                      sx={{
                        fontSize: 16,
                        fontFamily: "'Libre Franklin', sans-serif",
                        fontWeight: 400,
                        color: BRAND_TABLE_TEXT_COLOR,
                        textAlign: { xs: 'left', sm: textAlignSm },
                        width: 'max-content',
                      }}
                    >
                      {label}
                    </Typography>
                    {infoTexts[key] && (
                      <Tooltip title={infoTexts[key]}>
                        <InfoOutlinedIcon sx={{ fontSize: 14, color: '#bdbdbd', cursor: 'pointer' }} />
                      </Tooltip>
                    )}
                  </Box>
                  <Typography
                    sx={{
                      fontWeight: 700,
                      fontSize: 16,
                      color: BRAND_TABLE_TEXT_COLOR,
                      mt: 0.5,
                      fontFamily: "'Libre Franklin', sans-serif",
                      textAlign: { xs: 'left', sm: textAlignSm },
                      width: '100%',
                    }}
                  >
                    {formatValue(key, loanData[key as keyof Loan] as string | number | null | undefined)}
                  </Typography>
                </Box>
              </Grid>
            );
          })}
        </Grid>

        <Divider sx={{ mb: 3, borderColor: theme.palette.mode === 'dark' ? '#444' : '#eee' }} />

        {/* Financial details in two columns with vertical divider */}
        <Box sx={{ display: 'flex', gap: 4 }}>
          {/* Left Column */}
          <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 2 }}>
            <FieldItem
              label="Loan Type"
              value={formatValue('productName', loanType)}
              tooltip={infoTexts.productName || ''}
            />
            <FieldItem
              label="Loan Amount"
              value={formatValue('loanAmount', loanData.loanAmount)}
              tooltip={infoTexts.loanAmount || ''}
            />
             <FieldItem
              label="Loan Status"
              value={formatValue('status', loanData.status)}
              tooltip={infoTexts.status || ''}
            />
            <FieldItem
              label="Next Payment Date"
              value={formatValue('nextPaymentDate', loanData.nextPaymentDate)}
              tooltip={infoTexts.nextPaymentDate || ''}
            />
            <FieldItem
              label="Next Payment Amount"
              value={formatValue('nextPaymentAmount', loanData.nextPaymentAmount)}
              tooltip={infoTexts.nextPaymentAmount || ''}
            />
          </Box>

          {/* Vertical Divider */}
          <Divider
            orientation="vertical"
            flexItem
            sx={{
              borderColor: theme.palette.mode === 'dark' ? '#444' : '#eee',
              height: 'auto',
              alignSelf: 'stretch'
            }}
          />

          {/* Right Column */}
          <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 2 }}>
          <FieldItem
              label="Rate Type"
              value={formatValue('rateType', loanData.rateType)}
              tooltip={infoTexts.rateType || ''}
            />
          <FieldItem
              label="Interest Rate"
              value={formatValue('interestRate', loanData.interestRate)}
              tooltip={infoTexts.interestRate || ''}
            />
             <FieldItem
              label="Total Outstanding"
              value={formatValue('totalOutstandingAsOfDate', loanData.totalOutstandingAsOfDate)}
              tooltip={infoTexts.totalOutstandingAsOfDate || ''}
            />
            <FieldItem
              label="Principal Remaining"
              value={formatValue('principalRemaining', loanData.principalRemaining)}
              tooltip={infoTexts.principalRemaining || ''}
            />
            <FieldItem
              label="Interest Remaining"
              value={formatValue('interestRemaining', loanData.interestRemaining)}
              tooltip={infoTexts.interestRemaining || ''}
            />
            <FieldItem
              label="Total Outstanding Due Amount"
              value={formatValue('amountDueTillCurrent', loanData.amountDueTillCurrent)}
              tooltip={infoTexts.amountDueTillCurrent || ''}
            />
          </Box>
        </Box>
      </SummaryCard>
    </Box>
  );
};

export default SummaryTab;